// Shared lane theming used by Shop (?lane=) and Collection landing pages.
// THREE lanes:
//   - damp    : the main Wet Cabbage line (heavyweight hoodies/crewnecks/caps with brand wordmark)
//   - crimes  : apparel printed with community drawings (the worst artwork imaginable)
//   - jokes   : funny products & punny tees / stickers / mugs (display name: Accidentally Iconic)
// Tag override priority (Shopify product tags): damp > crimes > jokes.

export const LANE_CATEGORIES = {
  damp: ["sweatshirts", "crewnecks", "zip-hoodies"], // brand-led streetwear
  jokes: ["caps", "stickers", "mugs", "bags", "footwear", "briefs"],
  crimes: ["tees", "hoodies"],
};

export const LANE_THEMES = {
  damp: {
    title: "Mainline.",
    titleShort: "Mainline",
    kicker: "Lane one · the main line",
    blurb:
      "The heavyweight foundation. Premium streetwear — hoodies, crewnecks, caps — printed with the core Wet Cabbage wordmark. For people who've eaten a kebab in a car park with dignity.",
    page: "bg-[#1A3626]",
    heroText: "text-[#F9F9F4]",
    accent: "#93C065",
    underline: "#93C065",
    kickerColor: "text-[#93C065]",
    bannerBg: "bg-[#F9F9F4]",
    bannerText: "text-[#1A3626]",
    bannerCopy:
      "WET CABBAGE · MAINLINE · HEAVYWEIGHT · CAR-PARK CALIBRATED · NEVER FULLY DRY",
    cardBg: "bg-[#F9F9F4]",
    cardBorder: "border-[#93C065]",
    priceBg: "bg-[#1A3626]",
    priceColor: "text-[#F9F9F4]",
    priceShape: "",
    priceRotate: "-rotate-[3deg]",
    titleBg:
      "bg-gradient-to-t from-[#0F0F0F] via-[#0F0F0F]/75 to-transparent",
    badge: "MAIN",
  },
  crimes: {
    title: "Artistic Misconduct.",
    titleShort: "Misconduct",
    kicker: "Lane two · the worst artwork imaginable",
    blurb:
      "Premium heavyweight apparel printed with community drawings of weirdly drawn frogs, horses with five legs, and other emotionally unstable vegetables.",
    page: "bg-[#0F0F0F]",
    heroText: "text-[#F9F9F4]",
    accent: "#93C065",
    underline: "#93C065",
    kickerColor: "text-[#93C065]",
    bannerBg: "bg-[#1A3626]",
    bannerText: "text-[#F9F9F4]",
    bannerCopy:
      "EVIDENCE · ARTWORK FROM BEAUTIFULLY UNTRAINED ARTISTS · NO ART DEGREE HARMED",
    cardBg: "bg-[#F9F9F4]",
    cardBorder: "border-[#F9F9F4]",
    priceBg: "bg-[#0F0F0F]",
    priceColor: "text-[#F9F9F4]",
    priceShape: "",
    priceRotate: "rotate-[3deg]",
    titleBg:
      "bg-gradient-to-t from-[#1A3626] via-[#1A3626]/85 to-transparent",
    badge: "EXHIBIT",
  },
  jokes: {
    title: "Accidentally Iconic.",
    titleShort: "Iconic",
    kicker: "Lane three · funny stuff that escaped quality control",
    blurb:
      "Mugs that shouldn't exist, on-the-nose stickers, punny tees and caps. Designed for laughs first, breakfast second.",
    page: "bg-[#F4F2D9]",
    heroText: "text-[#0F0F0F]",
    accent: "#93C065",
    underline: "#93C065",
    kickerColor: "text-[#1A3626]",
    bannerBg: "bg-[#93C065]",
    bannerText: "text-[#0F0F0F]",
    bannerCopy: "WARNING · CONTAINS PUNS · NOT MEDICALLY ENDORSED · BEST CONSUMED HOT",
    cardBg: "bg-[#F9F9F4]",
    cardBorder: "border-[#1A3626]",
    priceBg: "bg-[#93C065]",
    priceColor: "text-[#0F0F0F]",
    priceShape: "rounded-full",
    priceRotate: "-rotate-[6deg]",
    titleBg:
      "bg-gradient-to-t from-[#0F0F0F] via-[#0F0F0F]/70 to-transparent",
    badge: "LOL",
  },
};

// Per-collection banner copy. Keyed by Shopify collection handle.
// When a collection page renders, this overrides the generic lane banner copy
// while still using the lane's colour theme.
export const COLLECTION_BANNERS = {
  "sip-happens":
    "DESIGNED IN SIX MINUTES · DYING MARKER PEN ENERGY · DISHWASHER SAFE · EMOTIONALLY UNSAFE",
  "kung-fu-chaos":
    "MASTER DONER KEBABIATI APPROVES · WAX ON · WAX OFF · WAX EVERYTHING · ROTISSERIE-CARRYING ENERGY ONLY",
  captology:
    "CAPS LOCK PERMANENTLY ENGAGED · BRIM ANGLE ADVISORY · UNVERIFIED HEADWEAR THESES · DO NOT WEAR INDOORS",
  "brief-encounters":
    "EYE CONTACT BRIEFLY · APOLOGIES PRINTED · LIMITED RUNS · LIMITED INTERACTIONS · NO FOLLOW-UP",
  "road-rage":
    "INDICATE · DO NOT INDICATE · SCREAM INTO STEERING WHEEL · MOTORWAY THERAPY · 4-WAY FLASHER ENERGY",
  "walk-of-shame":
    "ONE SOCK MISSING · SUNGLASSES INDOORS · 8AM ENERGY · NO EYE CONTACT BEFORE COFFEE · KEYS UNACCOUNTED FOR",
  "mildly-unacceptable":
    "BARELY APPROPRIATE · CONTAINS SHRUGS · WE TRIED · NOT QUITE OFFENSIVE · TOLERATED BY MOST",
};

// Inspect a single product and return its lane.
// 1. Explicit Shopify tag override (`damp` / `crimes` / `jokes`) wins.
// 2. Fall back to keyword-derived `kind`.
// Returns "damp" | "crimes" | "jokes" | null.
export function productLane(p) {
  if (!p) return null;
  if (p.lane === "damp" || p.lane === "jokes" || p.lane === "crimes") return p.lane;
  const kind = (p.kind || "").toLowerCase();
  if (LANE_CATEGORIES.damp.includes(kind)) return "damp";
  if (LANE_CATEGORIES.jokes.includes(kind)) return "jokes";
  if (LANE_CATEGORIES.crimes.includes(kind)) return "crimes";
  return null;
}

// Inspect a list of products and return the dominant lane key (or null if mixed/empty).
// Threshold: 60% of products in one lane to claim a theme.
export function detectLane(products = []) {
  if (!products || products.length === 0) return null;
  const buckets = { damp: 0, jokes: 0, crimes: 0 };
  for (const p of products) {
    const lane = productLane(p);
    if (lane) buckets[lane] += 1;
  }
  const total = buckets.damp + buckets.jokes + buckets.crimes;
  if (total === 0) return null;
  if (buckets.damp / total >= 0.6) return "damp";
  if (buckets.jokes / total >= 0.6) return "jokes";
  if (buckets.crimes / total >= 0.6) return "crimes";
  return null;
}
